/**
 * ============================================================================
 * SettingsNew - PRODUCTION-READY SETTINGS PAGE
 * ============================================================================
 *
 * Production-ready settings with:
 * - TanStack Query for data fetching & caching (NO POLLING - data is static)
 * - Mobile-responsive tab navigation
 * - Proper error/success messaging with auto-dismiss
 * - Form validation
 * - Optimistic UI updates
 *
 * REMOVED:
 * - Manual localStorage caching (replaced with TanStack Query)
 * - Polling (settings data doesn't change frequently)
 * - Redundant state management
 *
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import {
  User,
  Lock,
  CreditCard,
  Bell,
  Shield,
  Save,
  AlertCircle,
  CheckCircle,
  Loader,
  Mail,
  Phone,
  HelpCircle,
  MessageCircle,
  FileText,
  Users,
  Clock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useVenueRole } from '../hooks/useVenueRole';
import { useConfirmation } from '../contexts/ConfirmationContext';
import {
  useBankDetails,
  useUpdateProfile,
  useChangePassword,
  useRequestAccountDeletion,
} from '../hooks/useSettings';
import type {
  ProfileData,
  PasswordChangeData,
  SettingsTab,
} from '../types/settings';
import { isPasswordStrong, getPasswordChecks } from '../utils/passwordValidation';
import { PasswordStrengthIndicator } from '../components/ui/PasswordStrengthIndicator';

/**
 * Settings page - production-ready rebuild
 */
export default function SettingsNew() {
  const { user } = useAuth();
  const { confirm } = useConfirmation();
  const { isStaff } = useVenueRole();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  // ============================================================================
  // DATA FETCHING (TanStack Query)
  // ============================================================================

  const { data: bankData, isPending: bankLoading } = useBankDetails();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const requestDeletionMutation = useRequestAccountDeletion();

  // ============================================================================
  // PROFILE STATE
  // ============================================================================

  const [profileData, setProfileData] = useState<ProfileData>({
    name: '',
    email: '',
    phone: '',
  });
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Load profile data from AuthContext
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  // ============================================================================
  // PASSWORD STATE
  // ============================================================================

  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // ============================================================================
  // ACCOUNT DELETION STATE
  // ============================================================================

  const [deletionReason, setDeletionReason] = useState('');
  const [deletionSuccess, setDeletionSuccess] = useState('');
  const [deletionError, setDeletionError] = useState('');

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    try {
      await updateProfileMutation.mutateAsync(profileData);
      setProfileSuccess('Profile updated successfully!');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update profile';
      setProfileError(errorMessage);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordStrong(passwordData.new_password)) {
      const missing = getPasswordChecks(passwordData.new_password).filter(c => !c.met).map(c => c.label);
      setPasswordError(`Password is missing: ${missing.join(', ')}`);
      return;
    }

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordError('');
    setPasswordSuccess('');

    try {
      await changePasswordMutation.mutateAsync(passwordData);
      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to change password';
      setPasswordError(errorMessage);
    }
  };

  const handleAccountDeletion = async () => {
    if (!deletionReason.trim()) {
      setDeletionError('Please provide a reason for deletion');
      return;
    }

    const confirmed = await confirm({
      title: 'Request Account Deletion',
      message: 'Are you sure you want to request account deletion? This action will be reviewed by administrators.',
      confirmLabel: 'Request Deletion',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    setDeletionError('');
    setDeletionSuccess('');

    try {
      await requestDeletionMutation.mutateAsync({ reason: deletionReason });
      setDeletionSuccess(
        'Account deletion request submitted. An administrator will review your request.'
      );
      setDeletionReason('');
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to submit deletion request';
      setDeletionError(errorMessage);
    }
  };

  // ============================================================================
  // TABS CONFIGURATION
  // ============================================================================

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: User },
    { id: 'security' as const, label: 'Security', icon: Lock },
    // Payments = venue payout/bank info → owner-only, hidden from staff.
    ...(!isStaff ? [{ id: 'payments' as const, label: 'Payments', icon: CreditCard }] : []),
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
    { id: 'privacy' as const, label: 'Privacy', icon: Shield },
    { id: 'support' as const, label: 'Support', icon: HelpCircle },
  ];

  // ============================================================================
  // TAB CONTENT RENDERERS
  // ============================================================================

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            {/* Success/Error Messages */}
            {profileSuccess && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-green-900">{profileSuccess}</p>
              </div>
            )}

            {profileError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-900">{profileError}</p>
              </div>
            )}

            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) =>
                        setProfileData({ ...profileData, email: e.target.value })
                      }
                      required
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) =>
                        setProfileData({ ...profileData, phone: e.target.value })
                      }
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
                      placeholder="+234 803 123 4567"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={updateProfileMutation.isPending}
                  className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {updateProfileMutation.isPending ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            {/* Success/Error Messages */}
            {passwordSuccess && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-green-900">{passwordSuccess}</p>
              </div>
            )}

            {passwordError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-900">{passwordError}</p>
              </div>
            )}

            <div>
              <h4 className="text-lg font-black text-gray-900 mb-4">Change Password</h4>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Current Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, current_password: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
                    placeholder="Enter your current password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, new_password: e.target.value })
                    }
                    required
                    minLength={8}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
                    placeholder="Create a strong password"
                  />
                  <PasswordStrengthIndicator password={passwordData.new_password} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Confirm New Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={passwordData.new_password_confirmation}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        new_password_confirmation: e.target.value,
                      })
                    }
                    required
                    minLength={8}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
                    placeholder="Re-enter your new password"
                  />
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={changePasswordMutation.isPending}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {changePasswordMutation.isPending ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Changing...</span>
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4" />
                        <span>Change Password</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-black text-gray-900 mb-2">Payout Account Information</h4>
              <p className="text-sm text-gray-600 mb-4">
                Your payout account details. Contact administrators to update or change bank
                information.
              </p>

              {bankLoading ? (
                <div className="flex items-center justify-center p-12 bg-white rounded-xl border border-gray-200">
                  <Loader className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {bankData?.bank_name && bankData?.bank_account_number_masked ? (
                    <div className="p-4 sm:p-6 border border-gray-200 rounded-xl bg-gray-50">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <CreditCard className="h-6 w-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Bank Name</p>
                            <p className="font-black text-gray-900 text-lg">
                              {bankData.bank_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          {bankData.bank_details_verified ? (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-green-100 text-green-800">
                              <CheckCircle className="h-4 w-4 mr-1.5" />
                              Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-yellow-100 text-yellow-800">
                              <AlertCircle className="h-4 w-4 mr-1.5" />
                              Pending
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Account Number</p>
                          <p className="font-bold text-gray-900">
                            {bankData.bank_account_number_masked}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Account Name</p>
                          <p className="font-bold text-gray-900">
                            {bankData.bank_account_name || 'N/A'}
                          </p>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-300">
                        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-blue-900 mb-1">
                              Need to Update Your Bank Details?
                            </p>
                            <p className="text-sm text-blue-800 mb-2">
                              For security reasons, bank account changes must be approved by our
                              administrators.
                            </p>
                            <a
                              href="mailto:info@spottsapp.com?subject=Bank Details Update Request"
                              className="inline-flex items-center text-sm font-bold text-blue-600 hover:text-blue-700"
                            >
                              <Mail className="h-4 w-4 mr-1.5" />
                              Contact Admin to Change Details
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 text-center">
                      <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-900 font-bold mb-2">
                        No Payout Account Configured
                      </p>
                      <p className="text-sm text-gray-600 mb-4">
                        You don't have a payout account set up yet. Contact administrators to add
                        your bank details.
                      </p>
                      <a
                        href="mailto:info@spottsapp.com?subject=Setup Payout Account"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Contact Admin to Setup
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Info Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-6">
                <h5 className="font-black text-blue-900 mb-3 flex items-center text-sm sm:text-base">
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  About Payouts & Revenue
                </h5>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
                    <p>Payouts are processed automatically via Paystack</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
                    <p>Weekly automatic transfers to your verified bank account</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
                    <p>View your revenue breakdown and commission rates in the Revenue tab</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
                    <p>All transactions are secure and encrypted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bell className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-black text-blue-900 mb-2">
                    Email Notifications Active
                  </h4>
                  <p className="text-sm text-blue-800 mb-4">
                    You're currently receiving email notifications for all important events
                    related to your venue.
                  </p>
                  <div className="bg-white border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-bold text-gray-900 mb-3">
                      You receive notifications for:
                    </p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>New bookings</strong> - Instant alerts when customers book
                          your venue
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>Booking cancellations</strong> - Know immediately when
                          bookings are cancelled
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>Payment confirmations</strong> - Track all successful
                          payments and payouts
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>New reviews</strong> - Respond promptly to customer feedback
                        </span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>System updates</strong> - Important platform announcements
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-300 rounded-xl p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-gray-600 flex-shrink-0 mt-1" />
                <div>
                  <h5 className="font-black text-gray-900 mb-2">Notification Preferences</h5>
                  <p className="text-sm text-gray-700 mb-3">
                    All email notifications are sent to:{' '}
                    <strong>{user?.email || 'your registered email'}</strong>
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    To update your notification preferences or unsubscribe from specific
                    notifications, please contact support.
                  </p>
                  <a
                    href="mailto:info@spottsapp.com?subject=Update Notification Preferences"
                    className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-xl hover:bg-gray-900 font-semibold text-sm"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            {/* Success/Error Messages */}
            {deletionSuccess && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-green-900">{deletionSuccess}</p>
              </div>
            )}

            {deletionError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-900">{deletionError}</p>
              </div>
            )}

            <div>
              <h4 className="text-lg font-black text-gray-900 mb-4">Account Management</h4>
              <div className="p-4 sm:p-6 border-2 border-red-200 rounded-xl bg-red-50">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-black text-red-900 mb-2">Request Account Deletion</h5>
                    <p className="text-sm text-red-700 mb-4">
                      Submit a request to permanently delete your account and all associated
                      data. This request will be reviewed by our administrators.
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-bold text-gray-900 mb-2">
                    What happens when you request deletion?
                  </p>
                  <ul className="text-sm text-gray-700 space-y-1.5">
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span>
                        Your request will be marked as <strong>"Pending Deletion"</strong>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span>
                        An administrator will review your request within 2-3 business days
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span>You can still access your account while the request is pending</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span>Once approved, all your data will be permanently deleted</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span>
                        <strong>This action cannot be undone</strong>
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Reason for Deletion <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={deletionReason}
                    onChange={(e) => setDeletionReason(e.target.value)}
                    rows={4}
                    placeholder="Please tell us why you're requesting account deletion..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-red-500 text-sm"
                  />
                </div>

                <button
                  onClick={handleAccountDeletion}
                  disabled={requestDeletionMutation.isPending || !deletionReason.trim()}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {requestDeletionMutation.isPending ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Submitting Request...</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4" />
                      <span>Request Account Deletion</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      case 'support':
        return (
          <div className="space-y-6">
            {/* Contact Support Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-black text-gray-900">Support & Help</h3>
                <p className="text-gray-600">Get help from our support team</p>
              </div>
              <a
                href="mailto:info@spottsapp.com?subject=Vendor Support Request"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold"
              >
                <Mail className="h-4 w-4" />
                <span>Contact Support</span>
              </a>
            </div>

            {/* Quick Help Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <a
                href="tel:+2349160244454"
                className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <h4 className="text-base sm:text-lg font-black text-gray-900 ml-3">
                    Phone Support
                  </h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">Speak directly with our support team</p>
                <p className="text-lg font-bold text-gray-900">+234 916 024 4454</p>
                <div className="flex items-center text-sm text-gray-500 mt-2">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>Mon-Fri: 9 AM - 6 PM WAT</span>
                </div>
              </a>

              <a
                href="mailto:info@spottsapp.com"
                className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:border-green-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <h4 className="text-base sm:text-lg font-black text-gray-900 ml-3">
                    Email Support
                  </h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">Send us detailed questions</p>
                <p className="text-base sm:text-lg font-bold text-gray-900 break-all">
                  info@spottsapp.com
                </p>
                <div className="flex items-center text-sm text-gray-500 mt-2">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>Response within 24 hours</span>
                </div>
              </a>

              <a
                href="https://wa.me/2349160244454"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:border-purple-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MessageCircle className="h-6 w-6 text-purple-600" />
                  </div>
                  <h4 className="text-base sm:text-lg font-black text-gray-900 ml-3">WhatsApp</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">Quick responses via WhatsApp</p>
                <span className="text-purple-600 hover:text-purple-700 font-bold">
                  Chat Now →
                </span>
              </a>
            </div>

            {/* Common Issues */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h4 className="text-lg font-black text-gray-900 mb-4">
                Common Issues & Quick Fixes
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <h5 className="font-bold text-gray-900 mb-2">Payment Issues</h5>
                  <p className="text-sm text-gray-600">
                    If customers report payment failures, contact us via WhatsApp at{' '}
                    <a
                      href="https://wa.me/2349160244454"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      +234 916 024 4454
                    </a>
                  </p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                  <h5 className="font-bold text-gray-900 mb-2">Booking Conflicts</h5>
                  <p className="text-sm text-gray-600">
                    Double bookings? Use the Block Time Slot feature in Bookings to prevent conflicts, or check court settings.
                  </p>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                  <h5 className="font-bold text-gray-900 mb-2">Payout Questions</h5>
                  <p className="text-sm text-gray-600">
                    View your payout schedule and eligible bookings in the Payouts tab. Contact support for bank detail changes.
                  </p>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <h5 className="font-bold text-gray-900 mb-2">Manual Bookings</h5>
                  <p className="text-sm text-gray-600">
                    Record walk-in or phone bookings using the "Add Manual Booking" button in the Bookings tab.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center mb-6">
                <FileText className="h-6 w-6 text-gray-700 mr-2" />
                <h4 className="text-lg font-black text-gray-900">Frequently Asked Questions</h4>
              </div>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {[
                  {
                    category: 'Bookings',
                    q: 'How do I add a walk-in or phone booking?',
                    a: 'Go to Bookings tab and click "Add Manual Booking". You can record cash, transfer, or POS payments for walk-ins.',
                  },
                  {
                    category: 'Bookings',
                    q: 'Can I cancel or reschedule a booking?',
                    a: 'Yes. Find the booking in the Bookings tab, click on it to view details, then use Cancel or Reschedule options. Refunds are processed automatically for Spotts bookings.',
                  },
                  {
                    category: 'Payments',
                    q: 'When do I receive my payouts?',
                    a: 'Payouts are processed weekly. View your available balance and payout schedule in the Payouts tab.',
                  },
                  {
                    category: 'Courts',
                    q: 'How do I block a court for maintenance?',
                    a: 'Go to Bookings tab, click "Block Time Slot", select the court, date, and time range. You can block single or multiple days.',
                  },
                  {
                    category: 'Technical',
                    q: 'What if I forget my password?',
                    a: 'Click "Forgot Password" on the login page. Enter your email to receive a verification code, then set a new password.',
                  },
                ].map((faq, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                          Q
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {faq.category}
                          </span>
                        </div>
                        <h5 className="font-bold text-gray-900 mb-2">{faq.q}</h5>
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Still Need Help */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 sm:p-8 text-center">
              <Users className="h-12 w-12 text-white mx-auto mb-4" />
              <h4 className="text-xl sm:text-2xl font-black text-white mb-2">
                Still Need Help?
              </h4>
              <p className="text-blue-100 mb-6 text-sm sm:text-base">
                Our support team is here to help you succeed. We typically respond within 24
                hours.
              </p>
              <a
                href="mailto:info@spottsapp.com?subject=Vendor Support Request"
                className="bg-white text-blue-600 px-6 py-3 rounded-xl font-black hover:bg-blue-50 transition-colors inline-flex items-center"
              >
                <Mail className="h-5 w-5 mr-2" />
                Contact Support Team
              </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Settings</h1>
        <p className="text-gray-600 text-sm">Manage your account settings and preferences</p>
      </div>

      {/* Tabs Container */}
      <div className="bg-white rounded-xl border border-gray-200">
        {/* Tab Navigation - Mobile Responsive */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex gap-2 sm:gap-8 px-4 sm:px-6 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-2 border-b-2 font-semibold text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">{renderTabContent()}</div>
      </div>
    </div>
  );
}
